import { linkifyHtml } from '../utils/linkify';
import DOMPurify from 'dompurify';

import React, { useCallback } from 'react';
import { useContent } from '../hooks/useContent';
import { useSeo } from '../hooks/useSeo';
import { AdminControls } from '../components/AdminControls';
import type { PageContent, ProPlan } from '../types';
import { useToast } from '../hooks/useToast';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import * as authService from '../services/authService';
import { PayPalButtons, usePayPalScriptReducer } from "@paypal/react-paypal-js";
import { SUBSCRIPTION_PLANS } from '../constants';
import type { OnApproveData, OnApproveActions } from "@paypal/paypal-js";
import { XIcon } from '../components/icons/XIcon';

// A checkmark icon for feature lists
const CheckIcon = (props: React.SVGProps<SVGSVGElement>) => (
    <svg className={`h-6 w-6 text-green-500 flex-shrink-0 ${props.className || ''}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
    </svg>
);

export const PricingPage: React.FC = () => {
  const { content: allContent, isAdminMode, updatePageContent } = useContent();
  const [pageContent, setPageContent] = React.useState<PageContent>(allContent.pricing);
  const { showToast } = useToast();
  const { user, setUser, setModalView } = useAuth();
  const navigate = useNavigate();
  const [{ isPending, isRejected }] = usePayPalScriptReducer();

  const onSeoApplied = useCallback(() => {
    window.dispatchEvent(new Event('app-rendered'));
  }, []);

  useSeo(pageContent, 'pricing', onSeoApplied);

  React.useEffect(() => {
    if (allContent.pricing) {
        setPageContent(allContent.pricing);
    }
  }, [allContent.pricing]);

  const handleSave = async () => {
    const oldSlug = allContent.pricing?.seo?.slug;
    const redirectCreated = await updatePageContent('pricing', pageContent);
    showToast('Pricing page content saved!');
    if (redirectCreated) {
      const newSlug = pageContent.seo.slug;
      showToast(`Redirect created: ${oldSlug} -> ${newSlug}`);
    }
  };

  const onUpgradeSuccess = async (plan: ProPlan) => {
    if (user) {
      const updatedUser = await authService.updateUserProStatus(user.uid, true, plan);
      if (updatedUser) {
        setUser(updatedUser);
        navigate('/dashboard'); // Redirect to dashboard on success
      }
    }
  };

  const createSubscription = (data: Record<string, unknown>, actions: any, planId: string) => {
    if (!user) {
        showToast('Please log in or sign up to subscribe.');
        setModalView('login');
        return Promise.reject(new Error("User not logged in"));
    }
    return actions.subscription.create({ plan_id: planId });
  };

  const onApprove = (data: OnApproveData, actions: OnApproveActions, plan: ProPlan) => {
    showToast(`Subscription successful! Your subscription ID is ${data.subscriptionID}`);
    onUpgradeSuccess(plan);
    return Promise.resolve();
  };

  const onError = (err: any) => {
    console.error("PayPal Subscription Error:", err);
    // Do not show a generic error if it is a login issue, which is handled gracefully now.
    if (err && err.message && err.message.includes("User not logged in")) {
        return;
    }
    showToast("An error occurred with your subscription. Please try again.");
  };

  const onCancel = (data: Record<string, unknown>) => {
    showToast("You cancelled the subscription process.");
  };

  const sanitizedBody = pageContent?.body ? DOMPurify.sanitize(pageContent.body) : '';

  const monthlyPlanId = SUBSCRIPTION_PLANS.Monthly.id;
  const yearlyPlanId = SUBSCRIPTION_PLANS.Yearly.id;

  const renderPayPalButtons = (plan: ProPlan) => {
      if (isPending) return <div className="animate-pulse text-center">Loading Payment Options...</div>;
      if (isRejected) return <div className="text-red-600 font-semibold text-center">Failed to load payment options.</div>;
      if (!monthlyPlanId || !yearlyPlanId) return <div className="text-yellow-700 font-semibold text-center">Payments are currently unavailable.</div>;
      
      const planId = plan === 'Monthly' ? monthlyPlanId : yearlyPlanId;

      return (
        <PayPalButtons
            key={plan}
            style={{ layout: "vertical", label: "subscribe" }}
            createSubscription={(data, actions) => createSubscription(data, actions, planId)}
            onApprove={(data, actions) => onApprove(data, actions, plan)}
            onError={onError}
            onCancel={onCancel}
        />
      );
  };
  
  const renderSubscribeButton = () => (
      <button
          onClick={() => setModalView('signup')}
          className="w-full text-center py-3 px-6 bg-[#4F46E5] hover:bg-[#6366F1] text-white font-bold rounded-lg shadow-sm transition-all"
      >
          Sign Up to Subscribe
      </button>
  );


  return (
    <div className="w-full max-w-6xl mx-auto py-8 md:py-12 px-4 sm:px-6 lg:px-8 text-[#1F2937]">
      <div className="w-full max-w-5xl mx-auto">
        <header className="text-center mb-10">
          <h1 className="text-3xl md:text-4xl font-bold text-[#4F46E5]">
            {pageContent.h1}
          </h1>
          {sanitizedBody && (
            <div className="prose max-w-2xl mx-auto mt-4" dangerouslySetInnerHTML={{ __html: sanitizedBody }} />
          )}
          <p className="mt-4 text-lg text-indigo-700 font-medium max-w-2xl mx-auto">
            Unlock unlimited Branding Audits to validate your names, get consultant-grade insights, and download PDF reports.
          </p>
        </header>

        {isAdminMode && (
            <AdminControls 
                pageId="pricing" 
                pageContent={pageContent} 
                onContentChange={setPageContent}
                onSave={handleSave}
            />
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch mt-12">
            {/* Free Plan */}
            <div className="flex flex-col border border-gray-200 rounded-xl shadow-lg p-8">
                <h3 className="text-2xl font-bold">Free</h3>
                <p className="mt-2 text-gray-500">For trying out the basics.</p>
                <p className="mt-6 text-4xl font-extrabold">$0<span className="text-lg font-medium text-gray-500">/ forever</span></p>
                <ul className="mt-8 space-y-4 text-gray-600">
                    <li className="flex gap-3"><CheckIcon /> 2 Brand Name Generations</li>
                    <li className="flex gap-3"><CheckIcon /> 5 Names per Generation</li>
                    <li className="flex gap-3"><CheckIcon /> Download Names as PDF</li>
                    <li className="flex gap-3"><CheckIcon /> Free Prompt Generators</li>
                    <li className="flex gap-3 text-gray-400">
                        <XIcon className="h-6 w-6 text-gray-400 flex-shrink-0" /> 
                        <span>Branding Audits & Reports</span>
                    </li>
                </ul>
                <div className="mt-auto pt-8">
                    <Link to="/" className="block w-full text-center py-3 px-6 bg-gray-100 hover:bg-gray-200 text-[#1F2937] font-bold rounded-lg shadow-sm transition-all">
                        Get Started
                    </Link>
                </div>
            </div>

            {/* Yearly Plan (Most Popular) */}
            <div className="flex flex-col border-2 border-[#4F46E5] rounded-xl shadow-2xl p-8 relative">
                <div className="absolute top-0 -translate-y-1/2 left-1/2 -translate-x-1/2">
                    <span className="px-4 py-1 bg-[#4F46E5] text-white text-sm font-semibold rounded-full">Most Popular</span>
                </div>
                <h3 className="text-2xl font-bold">Pro Yearly</h3>
                <p className="mt-2 text-gray-500">For serious brand builders.</p>
                <p className="mt-6 text-4xl font-extrabold">$99.99<span className="text-lg font-medium text-gray-500">/ year</span></p>
                <p className="text-green-600 font-semibold mt-1">Save ~44%</p>
                <ul className="mt-8 space-y-4 text-gray-600">
                    <li className="flex gap-3"><CheckIcon /> Unlimited Generations</li>
                    <li className="flex gap-3"><CheckIcon /> Save Favorite Names</li>
                    <li className="flex gap-3"><CheckIcon /> View Generation History</li>
                    <li className="flex gap-3"><CheckIcon /> Free Prompt Generators</li>
                    <li className="flex gap-3 font-medium text-indigo-900 bg-indigo-50 p-2 -ml-2 rounded-md items-start">
                        <CheckIcon className="mt-1" />
                        <div className="flex flex-wrap gap-2 items-center">
                            <span>Unlimited Branding Audits</span>
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-indigo-200 text-indigo-800 whitespace-nowrap">Pro Feature</span>
                        </div>
                    </li>
                </ul>
                 <div className="mt-auto pt-8">
                    {user ? (
                        user.isPro && user.plan === 'Yearly' ? (
                            <p className="text-center font-semibold text-green-600">This is your current plan.</p>
                        ) : (
                            renderPayPalButtons('Yearly')
                        )
                    ) : (
                        renderSubscribeButton()
                    )}
                </div>
            </div>

            {/* Monthly Plan */}
            <div className="flex flex-col border border-gray-200 rounded-xl shadow-lg p-8">
                <h3 className="text-2xl font-bold">Pro Monthly</h3>
                <p className="mt-2 text-gray-500">Perfect for short-term projects.</p>
                <p className="mt-6 text-4xl font-extrabold">$14.99<span className="text-lg font-medium text-gray-500">/ month</span></p>
                <ul className="mt-8 space-y-4 text-gray-600">
                    <li className="flex gap-3"><CheckIcon /> Unlimited Generations</li>
                    <li className="flex gap-3"><CheckIcon /> Save Favorite Names</li>
                    <li className="flex gap-3"><CheckIcon /> View Generation History</li>
                    <li className="flex gap-3"><CheckIcon /> Free Prompt Generators</li>
                    <li className="flex gap-3 font-medium text-indigo-900 bg-indigo-50 p-2 -ml-2 rounded-md items-start">
                        <CheckIcon className="mt-1" />
                        <div className="flex flex-wrap gap-2 items-center">
                            <span>Unlimited Branding Audits</span>
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-indigo-200 text-indigo-800 whitespace-nowrap">Pro Feature</span>
                        </div>
                    </li>
                </ul>
                 <div className="mt-auto pt-8">
                     {user ? (
                        user.isPro && user.plan === 'Monthly' ? (
                            <p className="text-center font-semibold text-green-600">This is your current plan.</p>
                        ) : (
                            renderPayPalButtons('Monthly')
                        )
                    ) : (
                        renderSubscribeButton()
                    )}
                </div>
            </div>

        </div>

      </div>
    </div>
  );
};